import { Component } from '@theme/component';
import { isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';
import { SteamSystem } from './steam-system.js';

// ---------------------------------------------------------------------------
// Rim mask helper
// ---------------------------------------------------------------------------

/**
 * Build an SVG path `d` attribute for a rounded rectangle.
 * Radius is clamped so it never exceeds half the shorter side.
 * @param {number} x @param {number} y @param {number} w @param {number} h @param {number} r
 */
function roundedRectPath(x, y, w, h, r) {
  if (w <= 0 || h <= 0) return '';
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  return (
    `M${x + rr},${y}` +
    `H${x + w - rr}` +
    `A${rr},${rr} 0 0 1 ${x + w},${y + rr}` +
    `V${y + h - rr}` +
    `A${rr},${rr} 0 0 1 ${x + w - rr},${y + h}` +
    `H${x + rr}` +
    `A${rr},${rr} 0 0 1 ${x},${y + h - rr}` +
    `V${y + rr}` +
    `A${rr},${rr} 0 0 1 ${x + rr},${y}` +
    'Z'
  );
}

// ---------------------------------------------------------------------------
// Particle helpers
// ---------------------------------------------------------------------------

class Particle {
  x = 0;
  y = 0;
  vx = 0;
  vy = 0;
  life = 0;
  maxLife = 0;
  size = 0;
  growRate = 0;
  alpha = 1;
  r = 255;
  g = 255;
  b = 255;
  active = false;
  seq = 0;       // spawn sequence for stream ordering
}

class ParticlePool {
  /** @type {Particle[]} */
  #pool;
  #max;

  /** @param {number} max */
  constructor(max) {
    this.#max = max;
    this.#pool = Array.from({ length: max }, () => new Particle());
  }

  /** @returns {Particle|null} */
  acquire() {
    for (let i = 0; i < this.#max; i++) {
      if (!this.#pool[i].active) return this.#pool[i];
    }
    return null;
  }

  /** @param {(p: Particle) => void} fn */
  forEach(fn) {
    for (let i = 0; i < this.#max; i++) {
      if (this.#pool[i].active) fn(this.#pool[i]);
    }
  }
}

// ---------------------------------------------------------------------------
// Steam audio via Web Audio API
// ---------------------------------------------------------------------------

class SteamAudio {
  /** @type {AudioContext|null} */
  #ctx = null;
  /** @type {AudioBuffer|null} */
  #noiseBuffer = null;
  #enabled = true;

  // Continuous hiss state
  /** @type {AudioBufferSourceNode|null} */
  #continuousSource = null;
  /** @type {GainNode|null} */
  #continuousGain = null;
  /** @type {BiquadFilterNode|null} */
  #continuousFilter = null;
  #currentHissGain = 0;

  /** @param {boolean} enabled */
  constructor(enabled) {
    this.#enabled = enabled;
  }

  init() {
    if (this.#ctx || !this.#enabled) return;
    try {
      this.#ctx = new AudioContext();
      const sampleRate = this.#ctx.sampleRate;
      const length = sampleRate * 2; // 2 seconds of noise to reduce looping artifacts
      const buffer = this.#ctx.createBuffer(1, length, sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < length; i++) {
        data[i] = Math.random() * 2 - 1;
      }
      this.#noiseBuffer = buffer;
    } catch {
      this.#enabled = false;
    }
  }

  playHiss() {
    if (!this.#ctx || !this.#noiseBuffer || !this.#enabled) return;

    const now = this.#ctx.currentTime;
    const source = this.#ctx.createBufferSource();
    source.buffer = this.#noiseBuffer;

    const bandpass = this.#ctx.createBiquadFilter();
    bandpass.type = 'bandpass';
    bandpass.frequency.value = 6000;
    bandpass.Q.value = 0.8;

    const gain = this.#ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.25, now + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.8);

    source.connect(bandpass);
    bandpass.connect(gain);
    gain.connect(this.#ctx.destination);

    source.start(now);
    source.stop(now + 0.9);
  }

  startContinuousHiss() {
    if (!this.#ctx || !this.#noiseBuffer || !this.#enabled) return;
    this.stopContinuousHiss();

    const source = this.#ctx.createBufferSource();
    source.buffer = this.#noiseBuffer;
    source.loop = true;

    const filter = this.#ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 5000;
    filter.Q.value = 0.8;

    const gain = this.#ctx.createGain();
    gain.gain.value = 0;

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.#ctx.destination);
    source.start();

    this.#continuousSource = source;
    this.#continuousFilter = filter;
    this.#continuousGain = gain;
    this.#currentHissGain = 0;
  }

  /** @param {number} hitCount — number of drips hitting rocks this frame */
  updateContinuousHiss(hitCount) {
    if (!this.#continuousGain || !this.#continuousFilter) return;

    // Target gain from hit count via sqrt curve, capped at 0.15
    const targetGain = hitCount > 0
      ? Math.min(0.15, Math.sqrt(hitCount) * 0.06)
      : 0;

    // Smoothed: fast attack (0.15), slow release (0.05)
    const smoothing = targetGain > this.#currentHissGain ? 0.15 : 0.05;
    this.#currentHissGain += (targetGain - this.#currentHissGain) * smoothing;

    // Clamp very small values to zero to avoid residual noise
    if (this.#currentHissGain < 0.001) this.#currentHissGain = 0;

    this.#continuousGain.gain.value = this.#currentHissGain;

    // Modulate filter frequency with intensity (5000-6500Hz)
    const intensity = this.#currentHissGain / 0.15;
    this.#continuousFilter.frequency.value = 5000 + intensity * 1500;
  }

  stopContinuousHiss() {
    if (!this.#continuousGain || !this.#continuousSource) return;

    if (this.#ctx) {
      const now = this.#ctx.currentTime;
      this.#continuousGain.gain.setValueAtTime(this.#currentHissGain, now);
      this.#continuousGain.gain.linearRampToValueAtTime(0, now + 0.15);

      const src = this.#continuousSource;
      setTimeout(() => { try { src.stop(); } catch {} }, 200);
    } else {
      try { this.#continuousSource.stop(); } catch {}
    }

    this.#continuousSource = null;
    this.#continuousGain = null;
    this.#continuousFilter = null;
    this.#currentHissGain = 0;
  }

  dispose() {
    this.stopContinuousHiss();
    if (this.#ctx) {
      this.#ctx.close().catch(() => {});
      this.#ctx = null;
    }
  }
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export class SaunaSplashComponent extends Component {
  requiredRefs = ['canvas', 'rocks', 'bucket', 'ladleCursor', 'bowlWater', 'bucketWater', 'bgAudio', 'muteBtn'];

  /** @type {CanvasRenderingContext2D|null} */
  #ctx = null;
  #width = 0;
  #height = 0;
  #dpr = 1;
  /** @type {number|null} */
  #frameId = null;
  /** @type {ResizeObserver|null} */
  #resizeObserver = null;
  /** @type {AbortController|null} */
  #abortController = null;

  // Particle pools
  #waterPool = new ParticlePool(300);
  #steamPool = new ParticlePool(300);
  #splashPool = new ParticlePool(120);
  #wetPool = new ParticlePool(30);
  #spawnSeq = 0;

  // Water surface canvases
  /** @type {CanvasRenderingContext2D|null} */
  #bucketWaterCtx = null;
  /** @type {CanvasRenderingContext2D|null} */
  #bowlWaterCtx = null;

  // Drag state
  #isDragging = false;
  #pointerX = 0;
  #pointerY = 0;
  #bucketCenterX = 0;
  #bucketCenterY = 0;
  #bowlTilt = 0;
  #lastDragX = 0;
  #dripCounter = 0;
  #bowlScreenX = 0;
  #bowlScreenY = 0;
  #lastBowlScreenX = 0;
  #lastBowlScreenY = 0;
  #bowlVelocityX = 0;
  #bowlVelocityY = 0;
  #rocksRect = null;
  #dripHitCount = 0;
  #continuousHissActive = false;
  #wavePhase = 0;

  // Audio
  /** @type {SteamAudio|null} */
  #audio = null;
  #audioInitialized = false;
  #bgAudioStarted = false;
  #bgAudioMuted = true;

  // Hint
  #showHint = true;
  #hintAlpha = 1;

  // Ladle cursor
  #ladleDirection = 'right';

  // Local steam (video rim)
  /** @type {SteamSystem|null} */
  #localSteam = null;
  /** @type {MutationObserver|null} */
  #videoFrameStyleObserver = null;
  #rimWidthPx = 0;
  #rimHeightPx = 0;
  #ladleCupDown = false;
  #lastPointerX = 0;
  #ladleUrls = null;
  #cursorVisible = false;
  #isMobile = false;
  #pourAnimating = false;
  #ladleResetTimer = 0;

  connectedCallback() {
    super.connectedCallback();

    const canvas = /** @type {HTMLCanvasElement} */ (this.refs.canvas);
    this.#ctx = canvas.getContext('2d');
    this.#dpr = Math.min(window.devicePixelRatio || 1, 2);

    // Initialize water surface canvases
    const bwc = /** @type {HTMLCanvasElement} */ (this.refs.bucketWater);
    bwc.width = 160; bwc.height = 48;
    this.#bucketWaterCtx = bwc.getContext('2d');

    const bowlc = /** @type {HTMLCanvasElement} */ (this.refs.bowlWater);
    bowlc.width = 104; bowlc.height = 36;
    this.#bowlWaterCtx = bowlc.getContext('2d');

    const audioEnabled = this.dataset.audioEnabled !== 'false';
    this.#audio = new SteamAudio(audioEnabled);

    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    // Size canvas
    this.#resize();
    this.#resizeObserver = new ResizeObserver(() => this.#resize());
    this.#resizeObserver.observe(this);

    // Pointer events
    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    window.addEventListener('pointermove', this.#onPointerMove, { signal });
    window.addEventListener('pointerup', this.#onPointerUp, { signal });
    window.addEventListener('pointercancel', this.#onPointerUp, { signal });

    // Touch: prevent scrolling during drag
    this.addEventListener('touchmove', (e) => {
      if (this.#isDragging) e.preventDefault();
    }, { signal, passive: false });

    // Ladle cursor setup (desktop only)
    this.#isMobile = isMobileBreakpoint();
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange, { signal });

    // if (!this.#isMobile) {
    //   this.#initLadleCursor(signal);
    // }

    // Portal mute button + audio to <body> so they escape the
    // shopify-section stacking context (sticky sections with ascending z-index
    // would otherwise paint over a fixed-position element inside the first section).
    document.body.appendChild(this.refs.muteBtn);
    document.body.appendChild(this.refs.bgAudio);

    // Background audio mute toggle
    const bgAudio = /** @type {HTMLAudioElement} */ (this.refs.bgAudio);
    bgAudio.volume = 0.3;
    this.refs.muteBtn.addEventListener('click', () => this.#toggleBgAudio(), { signal });

    // Auto-start background audio on first user interaction
    // (skip if the mute button itself was clicked — let #toggleBgAudio handle it)
    const startBgAudio = (/** @type {Event} */ e) => {
      if (this.refs.muteBtn.contains(/** @type {Node} */ (e.target))) return;
      if (!this.#bgAudioStarted) {
        this.#bgAudioStarted = true;
        this.#bgAudioMuted = false;
        bgAudio.play().catch((err) => console.error('[sauna-splash] auto-start bgAudio.play() failed:', err));
        this.refs.muteBtn.classList.add('is-playing');
        this.refs.muteBtn.setAttribute('aria-label', 'Mute background audio');
      }
      document.removeEventListener('pointerdown', startBgAudio);
      document.removeEventListener('keydown', startBgAudio);
    };
    document.addEventListener('pointerdown', startBgAudio, { signal });
    document.addEventListener('keydown', startBgAudio, { signal });

    // Fade mute button when scrolled past first section
    const muteBtn = this.refs.muteBtn;
    const sectionHeight = this.offsetHeight;
    let muteBtnHeld = false;

    const updateFade = () => {
      if (muteBtnHeld) return;
      if (window.scrollY > sectionHeight) {
        muteBtn.classList.add('audio-toggle--faded');
      } else {
        muteBtn.classList.remove('audio-toggle--faded');
      }
    };

    window.addEventListener('scroll', updateFade, { signal, passive: true });

    muteBtn.addEventListener('mouseenter', () => {
      muteBtnHeld = true;
      muteBtn.classList.remove('audio-toggle--faded');
    }, { signal });

    muteBtn.addEventListener('mouseleave', () => {
      muteBtnHeld = false;
      updateFade();
    }, { signal });

    muteBtn.addEventListener('touchstart', () => {
      muteBtnHeld = true;
      muteBtn.classList.remove('audio-toggle--faded');
    }, { signal, passive: true });

    // On next scroll after tap, re-fade
    const onScrollAfterTap = () => {
      if (muteBtnHeld) {
        muteBtnHeld = false;
        updateFade();
      }
    };
    window.addEventListener('scroll', onScrollAfterTap, { signal, passive: true });

    // Local steam for video rim (only when a video is configured)
    const rimCanvas = /** @type {HTMLCanvasElement|null} */ (this.refs.rimCanvas);
    if (rimCanvas) {
      this.#localSteam = new SteamSystem(rimCanvas, this.#isMobile);
      this.#localSteam.observe(this);

      // Re-register mask whenever the merchant changes a range slider in the
      // theme editor (updates the inline style on the videoFrame element).
      const videoFrame = this.refs.videoFrame;
      if (videoFrame) {
        this.#videoFrameStyleObserver = new MutationObserver(() => this.#updateRimMask());
        this.#videoFrameStyleObserver.observe(videoFrame, {
          attributes: true,
          attributeFilter: ['style'],
        });
      }
    }

    // Start animation
    this.#animate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#frameId !== null) {
      cancelAnimationFrame(this.#frameId);
      this.#frameId = null;
    }
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }
    clearTimeout(this.#ladleResetTimer);
    // Return portalled elements and pause background audio
    const bgAudio = this.refs?.bgAudio;
    const muteBtn = this.refs?.muteBtn;
    if (bgAudio) {
      bgAudio.pause();
      if (bgAudio.parentNode === document.body) this.appendChild(bgAudio);
    }
    if (muteBtn && muteBtn.parentNode === document.body) this.appendChild(muteBtn);
    if (this.#audio) {
      this.#audio.dispose();
      this.#audio = null;
    }
    this.#videoFrameStyleObserver?.disconnect();
    this.#videoFrameStyleObserver = null;
    if (this.#localSteam) {
      this.#localSteam.dispose();
      this.#localSteam = null;
    }
  }

  // -----------------------------------------------------------------------
  // Resize
  // -----------------------------------------------------------------------

  #resize() {
    const canvas = /** @type {HTMLCanvasElement} */ (this.refs.canvas);
    const rect = this.getBoundingClientRect();
    this.#width = rect.width;
    this.#height = rect.height;
    canvas.width = this.#width * this.#dpr;
    canvas.height = this.#height * this.#dpr;
    canvas.style.width = `${this.#width}px`;
    canvas.style.height = `${this.#height}px`;
    if (this.#ctx) this.#ctx.setTransform(this.#dpr, 0, 0, this.#dpr, 0, 0);
    this.#rocksRect = null;

    const rimCanvas = /** @type {HTMLCanvasElement|null} */ (this.refs.rimCanvas);
    const videoFrame = /** @type {HTMLElement|null} */ (this.refs.videoFrame);
    if (this.#localSteam && rimCanvas && videoFrame) {
      // Size the rim canvas to just the outer-rim bounding box (video frame
      // plus `overflow` on all sides). This dramatically shortens particle
      // travel distance from canvas-bottom to canvas-top, so particles
      // reliably cross the rim and steam becomes visible passing over the
      // top edge of the video.
      const splashRect = this.getBoundingClientRect();
      const frameRect  = videoFrame.getBoundingClientRect();
      const fx = frameRect.left - splashRect.left;
      const fy = frameRect.top  - splashRect.top;
      const fw = frameRect.width;
      const fh = frameRect.height;

      const cs = getComputedStyle(videoFrame);
      const parseVar = (name, fallback) => {
        const v = parseFloat(cs.getPropertyValue(name));
        return Number.isFinite(v) ? v : fallback;
      };
      const overflow = parseVar('--sauna-video-overflow', 80);

      const canvasX = fx - overflow;
      const canvasY = fy - overflow;
      const canvasW = fw + 2 * overflow;
      const canvasH = fh + 2 * overflow;

      rimCanvas.style.left   = `${canvasX}px`;
      rimCanvas.style.top    = `${canvasY}px`;
      rimCanvas.style.width  = `${canvasW}px`;
      rimCanvas.style.height = `${canvasH}px`;

      this.#rimWidthPx  = canvasW;
      this.#rimHeightPx = canvasH;
      this.#localSteam.resize(this.#rimWidthPx, this.#rimHeightPx, this.#dpr);
      this.#updateRimMask();
    }
  }

  // -----------------------------------------------------------------------
  // Rim mask
  // -----------------------------------------------------------------------

  #updateRimMask() {
    const rimCanvas = /** @type {HTMLCanvasElement|null} */ (this.refs.rimCanvas);
    const frame = /** @type {HTMLElement|null} */ (this.refs.videoFrame);
    if (!rimCanvas || !frame) return;

    const cs = getComputedStyle(frame);
    const num = (name, fallback) => {
      const v = parseFloat(cs.getPropertyValue(name));
      return Number.isFinite(v) ? v : fallback;
    };
    const radius   = num('--sauna-video-radius',    16);
    const rimWidth = num('--sauna-video-rim-width', 120);
    const overflow = num('--sauna-video-overflow',   80);
    const fade     = num('--sauna-video-fade',       24);

    const canvasW = this.#rimWidthPx;
    const canvasH = this.#rimHeightPx;

    // Canvas is positioned so its top-left corresponds to the video frame's
    // top-left minus (overflow, overflow). Therefore in canvas-local coords:
    //   - Outer rim fills the canvas exactly: (0, 0) to (canvasW, canvasH).
    //     Outer corner radius = video radius + overflow.
    //   - Inner hole is offset by (overflow + rim_width) from each side.
    const outerX = 0;
    const outerY = 0;
    const outerW = canvasW;
    const outerH = canvasH;
    const outerR = radius + overflow;

    const innerInset = overflow + rimWidth;
    const innerX = innerInset;
    const innerY = innerInset;
    const innerW = canvasW - 2 * innerInset;
    const innerH = canvasH - 2 * innerInset;
    const innerR = Math.max(0, radius - rimWidth);

    const outer = roundedRectPath(outerX, outerY, outerW, outerH, outerR);
    const inner = innerW > 0 && innerH > 0
      ? roundedRectPath(innerX, innerY, innerW, innerH, innerR)
      : '';

    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${canvasW}" height="${canvasH}">` +
        `<defs><filter id="b" x="-50%" y="-50%" width="200%" height="200%">` +
          `<feGaussianBlur stdDeviation="${fade}"/>` +
        `</filter></defs>` +
        `<g filter="url(#b)">` +
          `<path fill="white" fill-rule="evenodd" d="${outer} ${inner}"/>` +
        `</g>` +
      `</svg>`;

    const url = `url("data:image/svg+xml;base64,${btoa(svg)}")`;
    rimCanvas.style.maskImage = url;
    rimCanvas.style.webkitMaskImage = url;
  }

  // -----------------------------------------------------------------------
  // Background audio toggle
  // -----------------------------------------------------------------------

  #toggleBgAudio() {
    const bgAudio = /** @type {HTMLAudioElement} */ (this.refs.bgAudio);
    if (this.#bgAudioMuted) {
      bgAudio.play().catch((err) => console.error('[sauna-splash] bgAudio.play() failed:', err));
      this.#bgAudioMuted = false;
      this.refs.muteBtn.classList.add('is-playing');
      this.refs.muteBtn.setAttribute('aria-label', 'Mute background audio');
    } else {
      bgAudio.pause();
      this.#bgAudioMuted = true;
      this.refs.muteBtn.classList.remove('is-playing');
      this.refs.muteBtn.setAttribute('aria-label', 'Unmute background audio');
    }
    this.#bgAudioStarted = true;
  }

  // -----------------------------------------------------------------------
  // Pointer handlers
  // -----------------------------------------------------------------------

  #onPointerDown = (/** @type {PointerEvent} */ e) => {
    // Initialise audio on first interaction (browser autoplay policy)
    if (!this.#audioInitialized && this.#audio) {
      this.#audio.init();
      this.#audioInitialized = true;
    }

    // Check if pointer is near the bucket
    const bucketRect = this.refs.bucket.getBoundingClientRect();
    const componentRect = this.getBoundingClientRect();
    const px = e.clientX - componentRect.left;
    const py = e.clientY - componentRect.top;

    // Expand hit area for easier grab
    const margin = 40;
    const bx = bucketRect.left - componentRect.left - margin;
    const by = bucketRect.top - componentRect.top - margin;
    const bw = bucketRect.width + margin * 2;
    const bh = bucketRect.height + margin * 2;

    if (px >= bx && px <= bx + bw && py >= by && py <= by + bh) {
      e.preventDefault();
      this.#isDragging = true;
      this.#bucketCenterX = bucketRect.left - componentRect.left + bucketRect.width / 2;
      this.#bucketCenterY = bucketRect.top - componentRect.top + bucketRect.height * 0.2;
      this.#pointerX = px;
      this.#pointerY = py;
      clearTimeout(this.#ladleResetTimer);
      this.#pourAnimating = false;
      this.#ladleCupDown = false;
      this.#updateLadleCursorSrc();
      this.#showHint = false;

      // Show bowl water in ladle
      this.refs.bowlWater.style.display = 'block';
      this.#lastDragX = px;
      this.#dripCounter = 0;
      this.#bowlTilt = 0;

      // Initialize velocity tracking (skip first frame)
      this.#lastBowlScreenX = 0;
      this.#lastBowlScreenY = 0;
      this.#bowlVelocityX = 0;
      this.#bowlVelocityY = 0;

      // Start continuous hiss audio
      if (this.#audio) this.#audio.startContinuousHiss();
      this.#continuousHissActive = true;

      // Capture pointer for reliable tracking
      this.setPointerCapture(e.pointerId);
    }
  };

  #onPointerMove = (/** @type {PointerEvent} */ e) => {
    if (!this.#isDragging) return;
    const rect = this.getBoundingClientRect();
    this.#pointerX = e.clientX - rect.left;
    this.#pointerY = e.clientY - rect.top;

    // Update ladle cursor during drag (mousemove is suppressed by pointer capture)
    if (this.#cursorVisible && !this.#isMobile) {
      const dx = this.#pointerX - this.#lastPointerX;
      if (Math.abs(dx) > 2) {
        this.#ladleDirection = dx > 0 ? 'right' : 'left';
      }
      this.#lastPointerX = this.#pointerX;

      const el = /** @type {HTMLImageElement} */ (this.refs.ladleCursor);
      const offsetX = this.#ladleDirection === 'left' ? -72 : -168;
      const offsetY = -26;
      el.style.transform = `translate(${this.#pointerX + offsetX}px, ${this.#pointerY + offsetY}px)`;
    }
  };

  #onPointerUp = (/** @type {PointerEvent} */ e) => {
    if (!this.#isDragging) return;
    this.#isDragging = false;

    // Stop continuous hiss
    if (this.#audio) this.#audio.stopContinuousHiss();
    this.#continuousHissActive = false;

    // Hide bowl water
    this.refs.bowlWater.style.display = 'none';

    // Ladle tips down (pouring), then returns upright after a delay
    this.#ladleCupDown = true;
    this.#pourAnimating = true;
    this.#updateLadleCursorSrc();
    this.#ladleResetTimer = setTimeout(() => {
      this.#pourAnimating = false;
      this.#ladleCupDown = false;
      this.#updateLadleCursorSrc();
    }, 400);

    try { this.releasePointerCapture(e.pointerId); } catch {}

    // Check collision with rocks
    const rocksRect = this.refs.rocks.getBoundingClientRect();
    const componentRect = this.getBoundingClientRect();
    const rx = rocksRect.left - componentRect.left;
    const ry = rocksRect.top - componentRect.top;
    const rw = rocksRect.width;
    const rh = rocksRect.height;

    if (
      this.#pointerX >= rx && this.#pointerX <= rx + rw &&
      this.#pointerY >= ry && this.#pointerY <= ry + rh
    ) {
      this.#triggerSplash(this.#pointerX, this.#pointerY);
      this.#triggerSteam(this.#pointerX, this.#pointerY);
      // Surge ambient steam (delegated to ember-background)
      const bg = document.querySelector('ember-background');
      if (bg) bg.triggerSurge(this.#pointerX / this.#width);
      this.#localSteam?.triggerSurge(this.#pointerX / this.#width);
      if (this.#audio) this.#audio.playHiss();
    }
  };

  // -----------------------------------------------------------------------
  // Ladle cursor
  // -----------------------------------------------------------------------

  /** @param {AbortSignal} signal */
  #initLadleCursor(signal) {
    this.#ladleUrls = {
      leftUp: this.dataset.ladleLeftUp,
      leftDown: this.dataset.ladleLeftDown,
      rightUp: this.dataset.ladleRightUp,
      rightDown: this.dataset.ladleRightDown,
    };

    // Bail if URLs are missing (e.g. assets not uploaded yet)
    if (!this.#ladleUrls.leftUp) return;

    // Preload all 4 images
    const preloads = Object.values(this.#ladleUrls).map((url) => {
      const img = new Image();
      img.src = url;
      return img.decode().catch(() => {});
    });

    // Once preloaded, activate the cursor
    Promise.all(preloads).then(() => {
      if (signal.aborted) return;

      // Set initial image
      this.#updateLadleCursorSrc();
      this.classList.add('ladle-cursor-active');

      // Mouse tracking events on the component
      this.addEventListener('mouseenter', this.#onLadleEnter, { signal });
      this.addEventListener('mouseleave', this.#onLadleLeave, { signal });
      this.addEventListener('mousemove', this.#onLadleMove, { signal });
      this.addEventListener('mousedown', this.#onLadleDown, { signal });
      this.addEventListener('mouseup', this.#onLadleUp, { signal });
    });
  }

  #teardownLadleCursor() {
    this.classList.remove('ladle-cursor-active');
    this.#cursorVisible = false;
    const el = /** @type {HTMLImageElement} */ (this.refs.ladleCursor);
    el.classList.remove('ladle-cursor--visible');
  }

  #handleBreakpointChange = () => {
    const wasMobile = this.#isMobile;
    this.#isMobile = isMobileBreakpoint();

    if (this.#localSteam && this.#isMobile !== wasMobile) {
      this.#localSteam.dispose();
      const rimCanvas = /** @type {HTMLCanvasElement} */ (this.refs.rimCanvas);
      this.#localSteam = new SteamSystem(rimCanvas, this.#isMobile);
      this.#localSteam.resize(this.#rimWidthPx, this.#rimHeightPx, this.#dpr);
      this.#localSteam.observe(this);
      this.#updateRimMask();
    }
  };

  #onLadleEnter = () => {
    if (this.#isMobile) return;
    this.#cursorVisible = true;
    const el = /** @type {HTMLImageElement} */ (this.refs.ladleCursor);
    el.classList.add('ladle-cursor--visible');
  };

  #onLadleLeave = () => {
    this.#cursorVisible = false;
    const el = /** @type {HTMLImageElement} */ (this.refs.ladleCursor);
    el.classList.remove('ladle-cursor--visible');
  };

  /** @param {MouseEvent} e */
  #onLadleMove = (e) => {
    if (this.#isMobile) return;

    // Hide ladle when hovering the button
    const overButton = e.target.closest('.content-overlay__button');
    const el = /** @type {HTMLImageElement} */ (this.refs.ladleCursor);
    if (overButton) {
      el.classList.remove('ladle-cursor--visible');
      this.classList.remove('ladle-cursor-active');
      return;
    }
    if (this.#cursorVisible) {
      el.classList.add('ladle-cursor--visible');
      this.classList.add('ladle-cursor-active');
    }

    const rect = this.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const dx = x - this.#lastPointerX;

    // 2px dead zone to prevent jitter
    if (Math.abs(dx) > 2) {
      this.#ladleDirection = dx > 0 ? 'right' : 'left';
    }
    this.#lastPointerX = x;

    // Position the cursor element — offset so the ladle bowl aligns with pointer
    const offsetX = this.#ladleDirection === 'left' ? -72 : -168;
    const offsetY = -26;
    el.style.transform = `translate(${x + offsetX}px, ${y + offsetY}px)`;
  };

  #onLadleDown = () => {
    if (this.#isDragging) return;
    if (this.#pourAnimating) {
      clearTimeout(this.#ladleResetTimer);
      this.#pourAnimating = false;
    }
    this.#ladleCupDown = true;
    this.#updateLadleCursorSrc();
  };

  #onLadleUp = () => {
    if (this.#pourAnimating) return;
    this.#ladleCupDown = false;
    this.#updateLadleCursorSrc();
  };

  #updateLadleCursorSrc() {
    if (!this.#ladleUrls) return;
    const el = /** @type {HTMLImageElement} */ (this.refs.ladleCursor);
    const dir = this.#ladleDirection;
    const down = this.#ladleCupDown;

    let url;
    if (dir === 'left') {
      url = down ? this.#ladleUrls.leftDown : this.#ladleUrls.leftUp;
    } else {
      url = down ? this.#ladleUrls.rightDown : this.#ladleUrls.rightUp;
    }

    if (el.src !== url) {
      el.src = url;
    }
  }

  // -----------------------------------------------------------------------
  // Spawn particles
  // -----------------------------------------------------------------------

  #spawnDrip() {
    this.#dripCounter++;

    // Speed-based drip rate: every 4th frame at rest → every frame when fast
    const speed = Math.sqrt(this.#bowlVelocityX ** 2 + this.#bowlVelocityY ** 2);
    const frameInterval = Math.max(1, Math.round(4 - speed * 0.3));
    if (this.#dripCounter % frameInterval !== 0) return;

    // Tilt increases leak rate
    const absTilt = Math.abs(this.#bowlTilt);
    const tiltLeakBonus = absTilt > 4 ? 1 : 0;

    // Multi-drip: 1 normally, 2-3 during extreme speed or heavy tilt
    const dripCount = speed > 8 ? 2 + Math.floor(Math.random() * 2) : 1 + tiltLeakBonus;

    for (let i = 0; i < dripCount; i++) {
      const p = this.#waterPool.acquire();
      if (!p) return;

      // Spread widens with speed: ±10px at rest → ±30px at speed
      const spread = 10 + Math.min(20, speed * 2.5);
      // Tilt biases spawn toward the low side
      const tiltOffset = this.#bowlTilt * 1.5;

      p.active = true;
      p.seq = this.#spawnSeq++;
      p.x = this.#bowlScreenX + tiltOffset + (Math.random() - 0.5) * spread;
      p.y = this.#bowlScreenY + 8 + Math.random() * 4;

      // Velocity inheritance: 40-70% of bowl horizontal velocity + tilt bias
      const inheritFactor = 0.4 + Math.random() * 0.3;
      const tiltVx = this.#bowlTilt * 0.08;
      p.vx = this.#bowlVelocityX * inheritFactor + tiltVx + (Math.random() - 0.5) * 0.3;
      p.vy = 0.5 + Math.random() * 0.5;

      p.life = 0;
      p.maxLife = 60 + Math.random() * 30;
      p.size = 1.5 + Math.random() * 1.5;
      p.growRate = 0;
      p.r = 168; p.g = 216; p.b = 234;
      p.alpha = 0.6;
    }
  }

  /** @param {number} x @param {number} y */
  #triggerSplash(x, y) {
    const count = 15 + Math.floor(Math.random() * 10);
    for (let i = 0; i < count; i++) {
      const p = this.#splashPool.acquire();
      if (!p) break;
      const angle = Math.random() * Math.PI * 2;
      const speed = 2 + Math.random() * 4;
      p.active = true;
      p.x = x + (Math.random() - 0.5) * 10;
      p.y = y + (Math.random() - 0.5) * 5;
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed - 2;
      p.life = 0;
      p.maxLife = 30 + Math.random() * 20;
      p.size = 1.5 + Math.random() * 2;
      p.growRate = 0;
      p.r = 200; p.g = 230; p.b = 255;
      p.alpha = 0.9;
    }
  }

  /** @param {number} x @param {number} y */
  #triggerSteam(x, y) {
    const count = 30 + Math.floor(Math.random() * 20);
    for (let i = 0; i < count; i++) {
      const p = this.#steamPool.acquire();
      if (!p) break;
      p.active = true;
      p.x = x + (Math.random() - 0.5) * 40;
      p.y = y + (Math.random() - 0.5) * 10;
      p.vx = (Math.random() - 0.5) * 0.8;
      p.vy = -(1 + Math.random() * 2.5);
      p.life = 0;
      p.maxLife = 90 + Math.random() * 60;
      p.size = 8 + Math.random() * 8;
      p.growRate = 0.08 + Math.random() * 0.06;
      p.r = 220; p.g = 220; p.b = 220;
      p.alpha = 0.5;
    }
  }

  /** @param {number} x @param {number} y */
  #spawnDripSteam(x, y) {
    const count = 2 + Math.floor(Math.random() * 3); // 2-4
    for (let i = 0; i < count; i++) {
      const p = this.#steamPool.acquire();
      if (!p) break;
      p.active = true;
      p.x = x + (Math.random() - 0.5) * 10;
      p.y = y + (Math.random() - 0.5) * 4;
      p.vx = (Math.random() - 0.5) * 0.5;
      p.vy = -(0.5 + Math.random() * 1.2);
      p.life = 0;
      p.maxLife = 40 + Math.random() * 30;
      p.size = 4 + Math.random() * 4;
      p.growRate = 0.04 + Math.random() * 0.03;
      p.r = 220; p.g = 220; p.b = 220;
      p.alpha = 0.3;
    }
  }

  /** @param {number} x @param {number} y */
  #spawnDripSplash(x, y) {
    const count = 1 + Math.floor(Math.random() * 2); // 1-2
    for (let i = 0; i < count; i++) {
      const p = this.#splashPool.acquire();
      if (!p) break;
      const angle = Math.random() * Math.PI; // upward hemisphere only
      const speed = 0.8 + Math.random() * 1.5;
      p.active = true;
      p.x = x + (Math.random() - 0.5) * 4;
      p.y = y;
      p.vx = Math.cos(angle) * speed;
      p.vy = -Math.sin(angle) * speed;
      p.life = 0;
      p.maxLife = 15 + Math.random() * 10;
      p.size = 1 + Math.random();
      p.growRate = 0;
      p.r = 200; p.g = 230; p.b = 255;
      p.alpha = 0.7;
    }
  }

  /** @param {number} x @param {number} y */
  #spawnWetMark(x, y) {
    const p = this.#wetPool.acquire();
    if (!p) return;
    p.active = true;
    p.x = x + (Math.random() - 0.5) * 6;
    p.y = y + (Math.random() - 0.5) * 3;
    p.vx = 0;
    p.vy = 0;
    p.life = 0;
    p.maxLife = 60 + Math.random() * 40;
    p.size = 4 + Math.random() * 5;
    p.growRate = 0;
    p.r = 120; p.g = 180; p.b = 220;
    p.alpha = 0.35;
  }

  // -----------------------------------------------------------------------
  // Update & draw
  // -----------------------------------------------------------------------

  #updateParticles() {
    const rr = this.#rocksRect;
    // Water
    this.#waterPool.forEach((p) => {
      p.life++;
      if (p.life >= p.maxLife) { p.active = false; return; }
      p.vy += 0.3; // gravity
      p.x += p.vx;
      p.y += p.vy;
      p.alpha = Math.max(0, 0.8 * (1 - p.life / p.maxLife));

      // Per-drip rock collision (only for downward-moving particles)
      if (rr && p.vy > 0 &&
        p.x >= rr.x && p.x <= rr.x + rr.w &&
        p.y >= rr.y && p.y <= rr.y + rr.h
      ) {
        p.active = false;
        this.#dripHitCount++;
        this.#spawnDripSteam(p.x, p.y);
        this.#spawnDripSplash(p.x, p.y);
        this.#spawnWetMark(p.x, p.y);
      }
    });

    // Splash
    this.#splashPool.forEach((p) => {
      p.life++;
      if (p.life >= p.maxLife) { p.active = false; return; }
      p.vy += 0.2;
      p.x += p.vx;
      p.y += p.vy;
      p.vx *= 0.98;
      p.alpha = Math.max(0, 0.9 * (1 - p.life / p.maxLife));
    });

    // Steam
    this.#steamPool.forEach((p) => {
      p.life++;
      if (p.life >= p.maxLife) { p.active = false; return; }
      p.x += p.vx + Math.sin(p.life * 0.05 + p.x * 0.01) * 0.3;
      p.y += p.vy;
      p.vy *= 0.995;
      p.size += p.growRate;
      const progress = p.life / p.maxLife;
      p.alpha = progress < 0.1
        ? (progress / 0.1) * 0.5
        : 0.5 * (1 - (progress - 0.1) / 0.9);
    });

    // Wet marks — static fade-out
    this.#wetPool.forEach((p) => {
      p.life++;
      if (p.life >= p.maxLife) { p.active = false; return; }
      p.alpha = 0.35 * (1 - p.life / p.maxLife);
    });
  }

  /** @param {CanvasRenderingContext2D} ctx */
  #drawWaterStream(ctx) {
    // Collect active water particles and sort by spawn sequence
    const particles = [];
    this.#waterPool.forEach((p) => particles.push(p));
    if (particles.length < 2) return;
    particles.sort((a, b) => a.seq - b.seq);

    // Group into chains: consecutive particles within proximity form a stream
    // A chain breaks when gap between sequential particles > threshold
    const maxGap = 40; // px - max distance between particles to stay in same stream
    const chains = [];
    let chain = [particles[0]];

    for (let i = 1; i < particles.length; i++) {
      const prev = particles[i - 1];
      const curr = particles[i];
      const dx = curr.x - prev.x;
      const dy = curr.y - prev.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < maxGap && curr.seq - prev.seq < 6) {
        chain.push(curr);
      } else {
        if (chain.length >= 2) chains.push(chain);
        chain = [curr];
      }
    }
    if (chain.length >= 2) chains.push(chain);

    // Draw each chain as a tapered line with gradient
    for (const pts of chains) {
      if (pts.length < 2) continue;

      ctx.save();
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      for (let i = 0; i < pts.length - 1; i++) {
        const a = pts[i];
        const b = pts[i + 1];

        // Width tapers: thicker near bowl (young), thinner as stream breaks
        const progress = i / pts.length;
        const baseWidth = 2.5;
        const width = baseWidth * (1 - progress * 0.6);

        // Alpha from both particles
        const alpha = Math.min(a.alpha, b.alpha) * 0.5;
        if (alpha < 0.01) continue;

        // Midpoint for quadratic curve control point (slight outward bow)
        const mx = (a.x + b.x) / 2 + (a.vx + b.vx) * 0.3;
        const my = (a.y + b.y) / 2;

        ctx.globalAlpha = alpha;
        ctx.strokeStyle = `rgba(150, 210, 235, 0.7)`;
        ctx.lineWidth = width;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.quadraticCurveTo(mx, my, b.x, b.y);
        ctx.stroke();

        // Inner bright core (thinner, whiter)
        ctx.globalAlpha = alpha * 0.6;
        ctx.strokeStyle = `rgba(200, 235, 250, 0.5)`;
        ctx.lineWidth = width * 0.4;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.quadraticCurveTo(mx, my, b.x, b.y);
        ctx.stroke();
      }

      ctx.restore();
    }
  }

  /** @param {CanvasRenderingContext2D} ctx */
  #drawParticles(ctx) {
    // --- Helper: glossy 3D radial gradient for a sphere -----------------------
    // Returns a radial gradient offset toward upper-left (light source).
    // cx, cy = center of the shape, radius = outer radius of gradient.
    const glossyGradient = (cx, cy, radius, r, g, b) => {
      const offX = -radius * 0.3;
      const offY = -radius * 0.3;
      const grad = ctx.createRadialGradient(
        cx + offX, cy + offY, 0,   // inner circle – specular highlight
        cx, cy, radius,             // outer circle – rim
      );
      // Bright specular
      grad.addColorStop(0.0, `rgba(255, 255, 255, 0.9)`);
      // Base color shifted ~30% toward white
      const br = Math.min(255, r + (255 - r) * 0.3) | 0;
      const bg = Math.min(255, g + (255 - g) * 0.3) | 0;
      const bb = Math.min(255, b + (255 - b) * 0.3) | 0;
      grad.addColorStop(0.3, `rgb(${br},${bg},${bb})`);
      // Actual color
      grad.addColorStop(0.7, `rgb(${r},${g},${b})`);
      // Darker rim
      grad.addColorStop(1.0, `rgb(${(r * 0.5) | 0},${(g * 0.5) | 0},${(b * 0.5) | 0})`);
      return grad;
    };

    // --- Wet marks (bottom layer) ---------------------------------------------
    const drawWetMark = (/** @type {Particle} */ p) => {
      ctx.globalAlpha = p.alpha;
      const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size);
      grad.addColorStop(0, `rgba(${p.r},${p.g},${p.b},${(p.alpha * 0.5).toFixed(3)})`);
      grad.addColorStop(1, `rgba(${p.r},${p.g},${p.b},0)`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    };

    this.#wetPool.forEach(drawWetMark);

    this.#drawWaterStream(ctx);

    // --- Water droplets (3D glossy with velocity stretching) -------------------
    const drawWaterDrop = (/** @type {Particle} */ p) => {
      ctx.globalAlpha = p.alpha;

      // Velocity-based stretching
      const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
      const stretch = 1 + Math.min(speed * 0.15, 1.5);
      const angle = Math.atan2(p.vy, p.vx);

      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(angle);

      // Compute specular offset in local space so highlight stays upper-left
      // regardless of rotation (fixed light source)
      const cosA = Math.cos(-angle);
      const sinA = Math.sin(-angle);
      const outerR = p.size * stretch;
      const hlWorld = outerR * 0.3;
      const hlX = -hlWorld * cosA - (-hlWorld) * sinA;
      const hlY = -hlWorld * sinA + (-hlWorld) * cosA;
      const grad = ctx.createRadialGradient(hlX, hlY, 0, 0, 0, outerR);
      grad.addColorStop(0.0, 'rgba(255, 255, 255, 0.9)');
      const br = Math.min(255, p.r + (255 - p.r) * 0.3) | 0;
      const bg = Math.min(255, p.g + (255 - p.g) * 0.3) | 0;
      const bb = Math.min(255, p.b + (255 - p.b) * 0.3) | 0;
      grad.addColorStop(0.3, `rgb(${br},${bg},${bb})`);
      grad.addColorStop(0.7, `rgb(${p.r},${p.g},${p.b})`);
      grad.addColorStop(1.0, `rgb(${(p.r * 0.5) | 0},${(p.g * 0.5) | 0},${(p.b * 0.5) | 0})`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.ellipse(0, 0, p.size * stretch, p.size, 0, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
    };

    this.#waterPool.forEach(drawWaterDrop);

    // --- Splash particles (3D glossy with outer glow) -------------------------
    const drawSplash = (/** @type {Particle} */ p) => {
      ctx.globalAlpha = p.alpha;

      // Outer glow halo
      const glowR = p.size * 1.8;
      const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, glowR);
      glow.addColorStop(0, `rgba(${p.r},${p.g},${p.b},0.15)`);
      glow.addColorStop(1, `rgba(${p.r},${p.g},${p.b},0)`);
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(p.x, p.y, glowR, 0, Math.PI * 2);
      ctx.fill();

      // Glossy sphere on top
      const grad = glossyGradient(p.x, p.y, p.size, p.r, p.g, p.b);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    };

    this.#splashPool.forEach(drawSplash);

    // Steam — soft blurred circles (unchanged)
    this.#steamPool.forEach((p) => {
      ctx.globalAlpha = p.alpha;
      const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size);
      grad.addColorStop(0, `rgba(${p.r},${p.g},${p.b},0.4)`);
      grad.addColorStop(1, `rgba(${p.r},${p.g},${p.b},0)`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    });

    ctx.globalAlpha = 1;
  }

  /** @param {CanvasRenderingContext2D} ctx */
  #drawHint(ctx) {
    if (!this.#showHint || this.#hintAlpha <= 0) return;

    // Fade out gradually
    this.#hintAlpha = Math.max(0, this.#hintAlpha - 0.002);
    if (this.#hintAlpha <= 0) return;

    const bucketRect = this.refs.bucket.getBoundingClientRect();
    const componentRect = this.getBoundingClientRect();
    const cx = bucketRect.left - componentRect.left + bucketRect.width / 2;
    const cy = bucketRect.top - componentRect.top - 15;
    const pulse = Math.sin(Date.now() * 0.004) * 0.3 + 0.7;

    ctx.save();
    ctx.globalAlpha = this.#hintAlpha * pulse * 0.6;
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 30);
    grad.addColorStop(0, 'rgba(168,216,234,0.6)');
    grad.addColorStop(1, 'rgba(168,216,234,0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, 30, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  #updateBowlWater() {
    const el = this.refs.bowlWater;

    // Sloshing tilt based on horizontal velocity
    const dx = this.#pointerX - this.#lastDragX;
    this.#lastDragX = this.#pointerX;

    // Target tilt: opposite to movement direction (inertia)
    const targetTilt = Math.max(-12, Math.min(12, -dx * 1.5));
    this.#bowlTilt += (targetTilt - this.#bowlTilt) * 0.12;

    // Calculate actual bowl position on screen.
    const isLeft = this.#ladleDirection === 'left';
    const ladleOffX = isLeft ? -72 : -168;
    const ladleOffY = -26;
    const bowlInImageX = isLeft ? 34 : 206;
    const bowlInImageY = 26;

    this.#bowlScreenX = this.#pointerX + ladleOffX + bowlInImageX;
    this.#bowlScreenY = this.#pointerY + ladleOffY + bowlInImageY;

    const halfW = 32; // half of 64px bowl-water element width
    const x = this.#bowlScreenX - halfW + 6;
    const y = this.#bowlScreenY - 5; // lowered to sit deeper in bowl

    // Wave physics — turbulence rises with speed, phase advances faster
    const speed = Math.sqrt(this.#bowlVelocityX ** 2 + this.#bowlVelocityY ** 2);
    const turbulence = Math.min(1, speed * 0.12);
    this.#wavePhase += 0.06 + speed * 0.04;

    el.style.transform = `translate(${x}px, ${y}px) rotate(${this.#bowlTilt.toFixed(1)}deg)`;

    // Render water surface on canvas
    this.#renderBowlWater(turbulence);

    // Compute bowl velocity (skip first frame when previous position isn't set)
    if (this.#lastBowlScreenX !== 0 || this.#lastBowlScreenY !== 0) {
      this.#bowlVelocityX = this.#bowlScreenX - this.#lastBowlScreenX;
      this.#bowlVelocityY = this.#bowlScreenY - this.#lastBowlScreenY;
    }
    this.#lastBowlScreenX = this.#bowlScreenX;
    this.#lastBowlScreenY = this.#bowlScreenY;
  }

  // -----------------------------------------------------------------------
  // Water surface rendering (canvas-based)
  // -----------------------------------------------------------------------

  #renderBucketWater() {
    const ctx = this.#bucketWaterCtx;
    if (!ctx) return;

    const w = 160;
    const h = 48;
    const cx = w / 2;
    const cy = h * 0.52;
    const rx = w * 0.44;
    const ry = h * 0.38;
    const t = performance.now() * 0.001;

    ctx.clearRect(0, 0, w, h);
    ctx.save();

    // Wavy elliptical surface path
    ctx.beginPath();
    const segs = 48;
    for (let i = 0; i <= segs; i++) {
      const a = (i / segs) * Math.PI * 2;
      let px = cx + Math.cos(a) * rx;
      let py = cy + Math.sin(a) * ry;

      // Gentle wave on the visible top edge
      const topFactor = Math.max(0, -Math.sin(a));
      const wave = topFactor * (
        Math.sin(a * 3 + t * 1.5) * 0.8 +
        Math.sin(a * 5 - t * 0.9) * 0.4 +
        Math.sin(a * 2 + t * 2.2) * 0.3
      );
      py += wave;

      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.clip();

    // Layer 1: Vertical depth (light surface → dark depths)
    const vGrad = ctx.createLinearGradient(cx, cy - ry, cx, cy + ry);
    vGrad.addColorStop(0, 'rgba(125, 195, 225, 0.72)');
    vGrad.addColorStop(0.25, 'rgba(85, 160, 205, 0.80)');
    vGrad.addColorStop(0.6, 'rgba(50, 120, 175, 0.86)');
    vGrad.addColorStop(1, 'rgba(22, 70, 125, 0.92)');
    ctx.fillStyle = vGrad;
    ctx.fillRect(0, 0, w, h);

    // Layer 2: Radial depth (edges lighter/transparent – shallow against walls)
    const rGrad = ctx.createRadialGradient(cx, cy, rx * 0.15, cx, cy, rx);
    rGrad.addColorStop(0, 'rgba(18, 55, 105, 0.22)');
    rGrad.addColorStop(0.55, 'rgba(30, 80, 130, 0.10)');
    rGrad.addColorStop(1, 'rgba(130, 195, 225, 0.0)');
    ctx.fillStyle = rGrad;
    ctx.fillRect(0, 0, w, h);

    // Layer 3: Specular highlight (slow sweep)
    const hlX = cx + Math.sin(t * 0.4) * rx * 0.22;
    const hlY = cy - ry * 0.18 + Math.cos(t * 0.3) * ry * 0.08;
    const hlGrad = ctx.createRadialGradient(hlX, hlY, 0, hlX, hlY, rx * 0.42);
    hlGrad.addColorStop(0, 'rgba(255, 255, 255, 0.32)');
    hlGrad.addColorStop(0.35, 'rgba(255, 255, 255, 0.10)');
    hlGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = hlGrad;
    ctx.fillRect(0, 0, w, h);

    // Layer 4: Caustic spots (dancing bright patches)
    for (let i = 0; i < 4; i++) {
      const phase = t * (0.5 + i * 0.18) + i * 1.7;
      const sx = cx + Math.sin(phase) * rx * 0.32;
      const sy = cy + Math.cos(phase * 1.1 + i) * ry * 0.32;
      const sr = 5 + Math.sin(phase * 0.6) * 2;
      const intensity = 0.06 + Math.sin(phase) * 0.025;
      const sg = ctx.createRadialGradient(sx, sy, 0, sx, sy, sr);
      sg.addColorStop(0, `rgba(255, 255, 255, ${intensity})`);
      sg.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = sg;
      ctx.beginPath();
      ctx.arc(sx, sy, sr, 0, Math.PI * 2);
      ctx.fill();
    }

    // Layer 5: Meniscus rim (bright edge where water meets wood)
    ctx.globalAlpha = 0.22;
    ctx.strokeStyle = 'rgba(175, 220, 242, 1)';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx - 1.5, ry - 1, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalAlpha = 1;

    ctx.restore();
  }

  /** @param {number} turbulence */
  #renderBowlWater(turbulence) {
    const ctx = this.#bowlWaterCtx;
    if (!ctx) return;

    const w = 104;
    const h = 36;
    const cx = w / 2;
    const cy = h * 0.52;
    const rx = w * 0.43;
    const ry = h * 0.36;
    const t = this.#wavePhase;
    const tilt = this.#bowlTilt;

    ctx.clearRect(0, 0, w, h);
    ctx.save();

    // Wavy ellipse — matches bucket wave formula + tilt/turbulence response
    ctx.beginPath();
    const segs = 48;
    for (let i = 0; i <= segs; i++) {
      const a = (i / segs) * Math.PI * 2;
      let px = cx + Math.cos(a) * rx;
      let py = cy + Math.sin(a) * ry;

      const topFactor = Math.max(0, -Math.sin(a));
      const wave = topFactor * (
        Math.sin(a * 3 + t * 1.5) * (0.8 + turbulence * 2.5) +
        Math.sin(a * 5 - t * 0.9) * (0.4 + turbulence * 1.2) +
        Math.sin(a * 2 + t * 2.2) * 0.3 +
        tilt * Math.cos(a) * 0.12
      );
      py += wave;

      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.clip();

    // Layer 1: Vertical depth (light surface → dark depths)
    const vGrad = ctx.createLinearGradient(cx, cy - ry, cx, cy + ry);
    vGrad.addColorStop(0, 'rgba(125, 195, 225, 0.72)');
    vGrad.addColorStop(0.25, 'rgba(85, 160, 205, 0.80)');
    vGrad.addColorStop(0.6, 'rgba(50, 120, 175, 0.86)');
    vGrad.addColorStop(1, 'rgba(22, 70, 125, 0.92)');
    ctx.fillStyle = vGrad;
    ctx.fillRect(0, 0, w, h);

    // Layer 2: Radial depth (edges lighter — shallow against walls)
    const rGrad = ctx.createRadialGradient(cx, cy, rx * 0.15, cx, cy, rx);
    rGrad.addColorStop(0, 'rgba(18, 55, 105, 0.22)');
    rGrad.addColorStop(0.55, 'rgba(30, 80, 130, 0.10)');
    rGrad.addColorStop(1, 'rgba(130, 195, 225, 0.0)');
    ctx.fillStyle = rGrad;
    ctx.fillRect(0, 0, w, h);

    // Layer 3: Specular highlight (shifts opposite to tilt)
    const hlX = cx - tilt * 0.7 + Math.sin(t * 0.4) * rx * 0.22;
    const hlY = cy - ry * 0.18 + Math.cos(t * 0.3) * ry * 0.08;
    const hlGrad = ctx.createRadialGradient(hlX, hlY, 0, hlX, hlY, rx * 0.42);
    hlGrad.addColorStop(0, 'rgba(255, 255, 255, 0.32)');
    hlGrad.addColorStop(0.35, 'rgba(255, 255, 255, 0.10)');
    hlGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = hlGrad;
    ctx.fillRect(0, 0, w, h);

    // Layer 4: Caustic spots (dancing bright patches)
    for (let i = 0; i < 3; i++) {
      const phase = t * (0.5 + i * 0.18) + i * 1.7;
      const sx = cx + Math.sin(phase) * rx * 0.32;
      const sy = cy + Math.cos(phase * 1.1 + i) * ry * 0.32;
      const sr = 3 + Math.sin(phase * 0.6) * 1.5;
      const intensity = 0.06 + Math.sin(phase) * 0.025;
      const sg = ctx.createRadialGradient(sx, sy, 0, sx, sy, sr);
      sg.addColorStop(0, `rgba(255, 255, 255, ${intensity})`);
      sg.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = sg;
      ctx.beginPath();
      ctx.arc(sx, sy, sr, 0, Math.PI * 2);
      ctx.fill();
    }

    // Layer 5: Meniscus rim (bright edge where water meets wood)
    ctx.globalAlpha = 0.22;
    ctx.strokeStyle = 'rgba(175, 220, 242, 1)';
    ctx.lineWidth = 1.0;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx - 1.5, ry - 1, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalAlpha = 1;

    ctx.restore();
  }

  // -----------------------------------------------------------------------
  // Animation loop
  // -----------------------------------------------------------------------

  #animate = () => {
    this.#frameId = requestAnimationFrame(this.#animate);

    const ctx = this.#ctx;
    if (!ctx) return;

    // Cache rocks bounding rect (single DOM read, invalidated on resize)
    // The .rocks-container is taller than the visible rocks (18% viewport height
    // but rocks only fill the bottom ~60%). Trim 40% off the top so collisions
    // only trigger where rocks are actually visible.
    if (!this.#rocksRect) {
      const rr = this.refs.rocks.getBoundingClientRect();
      const cr = this.getBoundingClientRect();
      const topTrim = rr.height * 0.4;
      this.#rocksRect = {
        x: rr.left - cr.left,
        y: rr.top - cr.top + topTrim,
        w: rr.width,
        h: rr.height - topTrim,
      };
    }

    // Reset per-frame drip hit counter
    this.#dripHitCount = 0;

    ctx.clearRect(0, 0, this.#width, this.#height);

    if (this.#isDragging) {
      this.#updateBowlWater();
      this.#spawnDrip();
    }

    this.#updateParticles();
    this.#drawParticles(ctx);

    // Render standing water surfaces
    this.#renderBucketWater();

    // Update continuous hiss gain based on drip-rock impacts this frame
    if (this.#audio && this.#continuousHissActive) {
      this.#audio.updateContinuousHiss(this.#dripHitCount);
    }

    this.#drawHint(ctx);

    if (this.#localSteam) {
      this.#localSteam.update(1 / 60);
      this.#localSteam.draw();
    }

    if (this.#cursorVisible) {
      this.#updateLadleCursorSrc();
    }
  };
}

if (!customElements.get('sauna-splash-component')) {
  customElements.define('sauna-splash-component', SaunaSplashComponent);
}
